import { useSEO } from "@/hooks/useSEO";
import { SeoPageLayout } from "@/components/seo/SeoPageLayout";
import { SeoSection, SeoList } from "@/components/seo/SeoContent";
import { Link } from "react-router-dom";

const SITE = "https://www.regard-de-nature.fr";

const NaturopatheToulouse = () => {
  useSEO({
    title: "Naturopathe Toulouse & Lauragais – Laura Elie",
    description:
      "Naturopathe près de Toulouse : accompagnement naturel pour stress, sommeil, fatigue, digestion, familles et enfants atypiques. Consultations en visio ou dans le Lauragais.",
    canonical: `${SITE}/naturopathe-toulouse`,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: "Regard de Nature – Laura Elie, Naturopathe près de Toulouse",
      image: `${SITE}/regard-de-nature-logo.png`,
      "@id": `${SITE}/naturopathe-toulouse`,
      url: `${SITE}/naturopathe-toulouse`,
      telephone: "+33637031866",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Montesquieu-Lauragais",
        postalCode: "31450",
        addressCountry: "FR",
      },
      areaServed: [
        "Toulouse",
        "Sud toulousain",
        "Lauragais",
        "Montesquieu-Lauragais",
        "Villefranche-de-Lauragais",
        "Castanet-Tolosan",
        "Ramonville-Saint-Agne",
        "Labège",
        "Baziège",
        "Nailloux",
      ],
      priceRange: "€€",
    },
  });

  return (
    <SeoPageLayout
      h1="Naturopathe près de Toulouse"
      intro="Un accompagnement naturopathique personnalisé pour les adultes, les enfants et les familles, en visio partout en France ou en présentiel dans le Lauragais, au sud-est de Toulouse."
    >
      <SeoSection title="Un accompagnement naturel depuis Toulouse et le sud toulousain">
        <p>
          Vous recherchez une <strong>naturopathe près de Toulouse</strong> pour mieux comprendre votre terrain,
          soutenir votre vitalité et retrouver un équilibre plus adapté à votre quotidien ?
        </p>
        <p>
          Je vous accompagne avec une approche globale, progressive et non médicale, centrée sur l'hygiène de vie :
          alimentation, sommeil, stress, digestion, rythme quotidien, émotions et ressources naturelles adaptées à
          votre situation.
        </p>
        <p>
          Les consultations sont possibles en <strong>visio</strong>, depuis Toulouse ou toute la France, ainsi qu'en
          présentiel à{" "}
          <Link to="/naturopathe-montesquieu-lauragais" className="text-primary underline hover:text-secondary">
            Montesquieu-Lauragais
          </Link>{" "}
          et à{" "}
          <Link to="/naturopathe-villefranche-de-lauragais" className="text-primary underline hover:text-secondary">
            Villefranche-de-Lauragais
          </Link>
          .
        </p>
      </SeoSection>

      <SeoSection title="Pour quels besoins ?">
        <SeoList
          items={[
            "Stress, charge mentale et rythme de vie intense",
            "Fatigue, baisse de vitalité ou récupération difficile",
            "Sommeil perturbé et besoin de retrouver des repères apaisants",
            "Digestion sensible et confort intestinal",
            "Accompagnement des enfants atypiques, TDAH, DYS ou hypersensibles",
            "Parentalité, équilibre familial et prévention de l'épuisement parental",
          ]}
        />
      </SeoSection>

      <SeoSection title="Consultations en visio pour Toulouse et partout en France">
        <p>
          Si vous habitez Toulouse, la métropole toulousaine ou plus loin, la consultation en visio permet d'être
          accompagné sans déplacement, dans un cadre familier et plus simple à organiser.
        </p>
        <p>
          Cette solution est particulièrement pratique lorsque le quotidien est chargé, lorsqu'il faut composer avec
          les horaires professionnels, les enfants, les trajets ou une fatigue importante.
        </p>
        <p>
          👉{" "}
          <Link to="/consultations-visio-naturopathe-france" className="text-primary underline hover:text-secondary">
            En savoir plus sur les consultations en visio
          </Link>
        </p>
      </SeoSection>

      <SeoSection title="Cabinets dans le Lauragais, près de Toulouse">
        <p>
          Pour les rendez-vous en présentiel, je vous reçois dans le Lauragais, au sud-est de Toulouse, dans deux
          lieux accessibles selon vos besoins et vos disponibilités.
        </p>
        <ul className="space-y-2">
          <li>
            👉{" "}
            <Link to="/naturopathe-montesquieu-lauragais" className="text-primary underline hover:text-secondary">
              Naturopathe à Montesquieu-Lauragais
            </Link>
          </li>
          <li>
            👉{" "}
            <Link to="/naturopathe-villefranche-de-lauragais" className="text-primary underline hover:text-secondary">
              Naturopathe à Villefranche-de-Lauragais
            </Link>
          </li>
        </ul>
      </SeoSection>

      <SeoSection title="Découvrir les accompagnements">
        <ul className="space-y-2">
          <li>
            👉 <Link to="/naturopathe-stress" className="text-primary underline hover:text-secondary">Stress</Link>
          </li>
          <li>
            👉 <Link to="/naturopathe-sommeil" className="text-primary underline hover:text-secondary">Sommeil</Link>
          </li>
          <li>
            👉 <Link to="/naturopathe-fatigue" className="text-primary underline hover:text-secondary">Fatigue</Link>
          </li>
          <li>
            👉 <Link to="/naturopathe-digestion" className="text-primary underline hover:text-secondary">Digestion & confort intestinal</Link>
          </li>
          <li>
            👉 <Link to="/tdah-dys-enfants-atypiques" className="text-primary underline hover:text-secondary">TDAH, DYS & enfants atypiques</Link>
          </li>
          <li>
            👉 <Link to="/parentalite-burn-out-parental" className="text-primary underline hover:text-secondary">Parentalité & burn-out parental</Link>
          </li>
          <li>
            👉 <Link to="/fleurs-de-bach" className="text-primary underline hover:text-secondary">Fleurs de Bach</Link>
          </li>
        </ul>
      </SeoSection>
    </SeoPageLayout>
  );
};

export default NaturopatheToulouse;